import { useState, useContext, useEffect } from "react";
import { flushSync } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/authcontext";
import { ContractService, ContractRequest } from "./contract.service";
import {
  showConfirmationAlert,
  showSuccessAlert,
  showErrorAlert,
} from "../../uiInterface/base-ui/alertservice";
import ContractForm from "./contractForm";
import { ContractFormDataProvider } from "./contractFormDataContext";
import companyLogo from "../../../../src/assets/company.png";
import { Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { formatCustomDateTime } from "../../../utils/dateUtils";
import TablePagination from "../../../components/pagination/TablePagination";
import {
  getLifecycleTypeName,
  getLifecycleTypePluralName,
} from "../../../utils/lifecycleUtils";
import { getApiErrorMessage } from "../../../utils/apiErrorUtils";

interface ContractsGridViewProps {
  contracts: any[];
  loading: boolean;
  error: string | null;
  currentView: "list" | "grid";
  onViewSwitch: (view: "list" | "grid") => void;

  pagination?: {
    currentPage: number;
    totalCount: number;
    pageSize: number;
  };
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (current: number, size: number) => void;
  onAddContract?: () => void;
}

const ContractsGridView = ({
  contracts,
  loading,
  error,
  pagination,
  onPageChange,
  onPageSizeChange,
  onAddContract,
}: ContractsGridViewProps) => {
  const navigate = useNavigate();
  const { selectedTenantId } = useContext(AuthContext);

  const [selectedContract, setSelectedContract] = useState<any | null>(null);
  const [isAddContractOpen, setIsAddContractOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formResetTrigger, setFormResetTrigger] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string>("");

  const closeOffcanvas = (offcanvasId: string) => {
    const modal = document.getElementById(offcanvasId);
    if (modal) {
      const closeButton = modal.querySelector(
        '[data-bs-dismiss="offcanvas"]'
      ) as HTMLElement;
      if (closeButton) {
        closeButton.click();
      } else {
        const bsOffcanvas = (window as any).bootstrap?.Offcanvas?.getInstance(
          modal
        );
        if (bsOffcanvas) {
          bsOffcanvas.hide();
        }
      }
    }
  };

  const handleDelete = async (contract: any) => {
    if (!selectedTenantId) return;

    const result = await showConfirmationAlert(
      `Delete ${getLifecycleTypeName("CONTRACT")}`,
      `Are you sure you want to delete "${contract.subject}"? This action cannot be undone.`,
      async () => {
        await ContractService.deleteContract(
          contract.contract_id,
          Number(selectedTenantId)
        );
      }
    );

    if (result.isConfirmed) {
      showSuccessAlert(
        `${getLifecycleTypeName("CONTRACT")} "${contract.subject}" deleted successfully!`
      );
      window.location.reload();
    }
  };

  const handleCreate = async (contract: Partial<ContractRequest>) => {
    setIsSubmitting(true);
    try {
      await ContractService.createContract(contract);
      setSelectedContract(null);
      setFormResetTrigger((prev) => prev + 1);
      closeOffcanvas("add_contract");

      setTimeout(() => {
        showSuccessAlert(`${getLifecycleTypeName("CONTRACT")} created successfully!`);
        window.location.reload();
      }, 500);
    } catch (err) {
      console.error("Failed to create contract:", err);
      showErrorAlert(getApiErrorMessage(err, `Failed to create ${getLifecycleTypeName("CONTRACT").toLowerCase()}`));
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async (
    id: number,
    contract: Partial<ContractRequest>
  ) => {
    setIsSubmitting(true);
    try {
      await ContractService.updateContract(id, contract);
      closeOffcanvas("edit_contract");
      setSelectedContract(null);

      setTimeout(() => {
        showSuccessAlert(`${getLifecycleTypeName("CONTRACT")} updated successfully!`);
        window.location.reload();
      }, 500);
    } catch (err) {
      console.error("Failed to update contract:", err);
      showErrorAlert(getApiErrorMessage(err, `Failed to update ${getLifecycleTypeName("CONTRACT").toLowerCase()}`));
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (contract: any) => {
    flushSync(() => {
      setSelectedContract(contract);
    });
  };

  const handlePreview = (contract: any) => {
    navigate(`/contracts/${contract.contract_id}`);
  };

  const handleFormSubmit = async (formData: Partial<ContractRequest>) => {
    if (selectedContract) {
      await handleUpdate(selectedContract.contract_id, formData);
    } else {
      await handleCreate(formData);
    }
  };

  const formatCurrency = (amount: number, currencyCode: string) => {
    if (!amount) return "Not Set";
    return `${currencyCode} ${amount.toLocaleString()}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "Not Set";
    return formatCustomDateTime(dateString);
  };

  const gridStyles = `
    .contract-grid-row {
      margin-right: -10px;
      margin-left: -10px;
    }

    .contract-grid-row > div {
      padding-right: 10px;
      padding-left: 10px;
      margin-bottom: 20px;
    }

    .contract-card {
      height: 100%;
      display: flex;
      flex-direction: column;
    }

    .contract-card .card-body {
      flex: 1;
      display: flex;
      flex-direction: column;
    }

    .contract-card .card-content {
      flex: 1;
    }

    .hover-primary:hover {
      color: #824eeb !important;
      transition: color 0.2s ease;
    }

    @media (max-width: 575px) {
      .contract-grid-row > div {
        width: 100%;
      }
    }
  `;

  useEffect(() => {
    if (typeof document !== "undefined") {
      const styleElement = document.createElement("style");
      styleElement.textContent = gridStyles;
      if (!document.head.querySelector("style[data-contract-grid]")) {
        styleElement.setAttribute("data-contract-grid", "true");
        document.head.appendChild(styleElement);
      }
    }
  }, []);

  useEffect(() => {
    const editOffcanvas = document.getElementById("edit_contract");

    const handleHidden = () => {
      setSelectedContract(null);
    };

    if (editOffcanvas) {
      editOffcanvas.addEventListener("hidden.bs.offcanvas", handleHidden);
    }

    return () => {
      if (editOffcanvas) {
        editOffcanvas.removeEventListener("hidden.bs.offcanvas", handleHidden);
      }
    };
  }, []);

  useEffect(() => {
    const addOffcanvas = document.getElementById("add_contract");

    const handleHidden = () => {
      setIsAddContractOpen(false);
    };

    if (addOffcanvas) {
      addOffcanvas.addEventListener("hidden.bs.offcanvas", handleHidden);
    }

    return () => {
      if (addOffcanvas) {
        addOffcanvas.removeEventListener("hidden.bs.offcanvas", handleHidden);
      }
    };
  }, []);

  const renderContractCard = (contract: any) => (
    <div key={contract.contract_id} className="col-xxl-3 col-xl-4 col-md-6">
      <div className="card border h-100">
        <div className="card-body d-flex flex-column">
          <div className="d-flex align-items-center justify-content-between pb-3 border-bottom mb-3">
            <div>
              <h5 className="mb-1">
                <Link
                  to={`/contracts/${contract.contract_id}`}
                  className="text-dark text-decoration-none hover-primary"
                >
                  {contract.subject?.length > 20 ? (
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id={`tooltip-${contract.contract_id}`}>{contract.subject}</Tooltip>}
                    >
                      <span>{`${contract.subject.substring(0, 20)}...`}</span>
                    </OverlayTrigger>
                  ) : (
                    contract.subject
                  )}
                </Link>
              </h5>
            </div>
            <div className="dropdown table-action">
              <Link
                to="#"
                className="action-icon bg-white"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="fa fa-ellipsis-v" />
              </Link>
              <div className="dropdown-menu dropdown-menu-right">
                <Link
                  className="dropdown-item"
                  to="#"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#edit_contract"
                  onClick={() => handleEdit(contract)}
                >
                  <i className="ti ti-edit text-blue" /> Edit
                </Link>
                <Link
                  className="dropdown-item"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleDelete(contract);
                  }}
                >
                  <i className="ti ti-trash text-danger" /> Delete
                </Link>
                <Link
                  className="dropdown-item"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handlePreview(contract);
                  }}
                >
                  <i className="ti ti-clipboard-copy text-blue-light" /> View{" "}
                  {getLifecycleTypeName("CONTRACT")}
                </Link>
              </div>
            </div>
          </div>
          <div className="d-block">
            <div className="mb-3">
              <p className="d-flex align-items-center mb-2">
                <span className="avatar avatar-xs border text-dark me-2">
                  <i className="ti ti-calendar-event fs-12" />
                </span>
                Due Date: {formatDate(contract.due_datetime)}
              </p>
              <p className="d-flex align-items-center">
                <span className="avatar avatar-xs border text-dark me-2">
                  <i className="ti ti-calendar-stats" />
                </span>
                Expiration Date: {formatDate(contract.expiration_datetime)}
              </p>
            </div>
            <div className="d-flex align-items-center mt-auto mb-2 p-3" style={{ backgroundColor: "#f7f8f9", borderRadius: '6px' }}>
              <Link to="#" className="avatar avatar-sm bg-white border me-2">
                <img
                  src={contract.company?.avatar_url || companyLogo}
                  alt={contract.company?.company_name || "Company"}
                  className="w-100 h-100 rounded"
                  style={{ objectFit: "cover" }}
                  onError={(e) => {
                    e.currentTarget.src = companyLogo;
                  }}
                />
              </Link>
              <div className="d-flex flex-column">
                <Link
                  to={`/companies/${contract.company?.company_id}`}
                  className="text-dark text-decoration-none hover-primary"
                >
                  {contract.company?.company_name || "No Company"}
                </Link>
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-between pt-3 border-top">
            <div>
              <span className="badge badge-purple-light">
                <i className="ti ti-moneybag me-1" /> Amount:{" "}
                {formatCurrency(contract.amount, contract.currency_code)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="card-body">
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="mt-2">
            <small className="text-muted">Loading {getLifecycleTypePluralName("CONTRACT").toLowerCase()}...</small>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-body">
        <div className="alert alert-danger text-center">
          <i className="ti ti-alert-circle me-2"></i>
          {error}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="card-body">
        <div className="row contract-grid-row">
          {contracts.length === 0 ? (
            <div className="col-12">
              <div className="text-center py-5">
                <div className="avatar avatar-xl bg-light-gray rounded-circle mb-3 mx-auto">
                  <i className="ti ti-file-certificate fs-36 text-gray"></i>
                </div>
                <h4 className="text-gray mb-2">No {getLifecycleTypePluralName("CONTRACT")} Found!</h4>
                <p className="text-muted mb-3">
                  There are no {getLifecycleTypePluralName("CONTRACT").toLowerCase()} to display at the moment.
                </p>
                <button
                  className="btn btn-primary"
                  data-bs-toggle="offcanvas"
                  data-bs-target={onAddContract ? "#offcanvas_add_contract" : "#add_contract"}
                  onClick={() => (onAddContract ? onAddContract() : setIsAddContractOpen(true))}
                >
                  <i className="fa fa-plus me-2" />
                  Add Your First {getLifecycleTypeName("CONTRACT")}
                </button>
              </div>
            </div>
          ) : (
            contracts.map((contract) => renderContractCard(contract))
          )}
        </div>

        {/* Pagination for Grid View */}
        {pagination && onPageChange && contracts.length !== 0 && (
          <TablePagination
            currentPage={pagination.currentPage}
            pageSize={pagination.pageSize}
            totalItems={pagination.totalCount}
            filteredItems={pagination.totalCount}
            isFiltered={false}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange || (() => { })}
            entityName={getLifecycleTypePluralName("CONTRACT")}
            loading={loading}
          />
        )}
      </div>

      {/* Add Contract Offcanvas */}
      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        id="add_contract"
        style={{ width: "550px" }}

      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">Add New {getLifecycleTypeName("CONTRACT")}</h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {isAddContractOpen && (
            <ContractFormDataProvider isMainApiCompleted={true}>
              <ContractForm
                key={`create-new-contract-${formResetTrigger}`}
                onSubmit={handleFormSubmit}
                onCancel={() => closeOffcanvas("add_contract")}
                isSubmitting={isSubmitting}
                resetTrigger={formResetTrigger}
              />
            </ContractFormDataProvider>
          )}
        </div>
      </div>

      {/* Edit Contract Offcanvas */}
      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        id="edit_contract"
        style={{ width: "550px" }}

      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">Edit {getLifecycleTypeName("CONTRACT")}</h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {selectedContract && (
            <ContractFormDataProvider isMainApiCompleted={true}>
              <ContractForm
                contract={selectedContract}
                onSubmit={handleFormSubmit}
                isSubmitting={isSubmitting}
                onCancel={() => {
                  setSelectedContract(null);
                  closeOffcanvas("edit_contract");
                }}
                isMainApiCompleted={true}
              />
            </ContractFormDataProvider>
          )}
        </div>
      </div>

      {/* Delete Contract Modal Removed */}
    </>
  );
};

export default ContractsGridView;
